import { type FC, useMemo, useState } from "react";
import { Dialog, LinearProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  useProductQuery,
  useUpdateProductMutation,
} from "@/partner/entities/products";
import {
  ComplianceScreen,
  ComplianceWarning,
  useComplianceEnabled,
} from "@/partner/features/compliance";
import { useAbility } from "@/partner/shared/lib";
import {
  ProductTypeType,
  type CreateProductInputType,
  type UpdateProductInputType,
} from "@/types/graphql/partner.generated";
import { mapProductToFormValues } from "../model";
import { ProductForm } from "./ProductForm";
import { ProductTabsMenu } from "./ProductTabsMenu";
import { ProductActionsMenu } from "./ProductActionsMenu";
import { CancelEventDialog } from "./CancelEventDialog";
import { EventCancelProgressCard } from "./EventCancelProgressCard";

interface EditProductDialogProps {
  open: boolean;
  productId: string | null;
  /** Активная вкладка карточки (?tab=): "edit" | "records2257". */
  tab?: string;
  onClose: () => void;
  onCompleted?: () => void;
}

/**
 * Диалог редактирования продукта (референс: EditProductDialog.js) —
 * network-only запрос (не доверяем кэшу — цены/окна могут поменяться на
 * бэкенде), LinearProgress, форма рендерится только когда данные пришли.
 */
export const EditProductDialog: FC<EditProductDialogProps> = ({
  open,
  productId,
  tab = "edit",
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  // référence: Edit.js `{error && <ErrorMessage error={error} />}` — ошибка
  // мутации показывается ещё и персистентным алертом внутри формы (не только
  // снекбаром), пока пользователь не попробует сохранить снова.
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data, loading } = useProductQuery({
    fetchPolicy: "network-only",
    variables: { id: String(productId) },
    skip: !productId || !open,
  });

  const [updateProduct, { loading: updating }] = useUpdateProductMutation({
    onCompleted: () => {
      setErrorMessage(null);
      enqueueSnackbar(t("products.edit.success"), { variant: "success" });
      onCompleted?.();
    },
    onError: (error) => {
      setErrorMessage(error.message);
      enqueueSnackbar(error.message, { variant: "error" });
    },
    refetchQueries: ["ProductsList"],
  });

  const initialValues = useMemo(
    () => (data?.product ? mapProductToFormValues(data.product) : undefined),
    [data]
  );

  // ProductForm с isEdit строит вход через buildUpdateProductInput
  // (UpdateProductInputType, БЕЗ `type` — Product.type immutable, см. C1 в
  // финальном ревью: раньше сюда прилетал CreateProductInputType с `type`,
  // и GraphQL отклонял весь запрос на updateProduct).
  const handleSubmit = async (
    input: CreateProductInputType | UpdateProductInputType
  ) => {
    await updateProduct({
      variables: {
        id: String(productId),
        input: input as UpdateProductInputType,
      },
    });
  };

  const complianceEnabled = useComplianceEnabled();
  const ability = useAbility();
  const [cancelOpen, setCancelOpen] = useState(false);
  const product = data?.product;
  // Отмена события: только PPV, только с правом EventCancel (admin
  // организации), пока отмены не было. Ход отмены виден всем, кто видит
  // продукт, после её старта.
  const canCancelEvent =
    Boolean(productId) &&
    product?.type === ProductTypeType.PpvType &&
    !product.eventCancel &&
    ability.can("create", "EventCancel");

  // Вкладок до фазы 4 у продукта не было: показываем их только когда есть
  // что переключать, иначе карточка продукта остаётся прежней.
  const menu =
    complianceEnabled && productId ? (
      <>
        <ProductTabsMenu tab={tab} />
        <ComplianceWarning
          productId={productId}
          published={Boolean(data?.product?.publishAt)}
        />
      </>
    ) : null;

  return (
    <Dialog open={open} onClose={onClose} fullScreen scroll="body">
      {complianceEnabled && tab === "records2257" && productId ? (
        <ComplianceScreen productId={productId} onClose={onClose} menu={menu} />
      ) : (
        <>
          {menu}
          {loading && <LinearProgress />}

          {data?.product && (
            <ProductForm
              title={t("products.edit.title")}
              loading={updating}
              error={errorMessage}
              isEdit
              isDraft={!data.product.publishAt}
              status={data.product.status}
              initialValues={initialValues}
              metadata={{ siteUrl: data.product.siteUrl }}
              onSubmit={handleSubmit}
              onClose={onClose}
              headerMenu={
                canCancelEvent ? (
                  <ProductActionsMenu
                    onCancelEvent={() => setCancelOpen(true)}
                  />
                ) : null
              }
              notice={
                data.product.eventCancel && productId ? (
                  <EventCancelProgressCard productId={productId} />
                ) : null
              }
            />
          )}
          {productId && canCancelEvent && (
            <CancelEventDialog
              productId={productId}
              open={cancelOpen}
              onClose={() => setCancelOpen(false)}
              onStarted={() => setCancelOpen(false)}
            />
          )}
        </>
      )}
    </Dialog>
  );
};
